import { Info } from '@mui/icons-material';
import {
    Box,
    Button,
    Stack,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomstructureset';
import { environmentState, mainViewState } from '~/state/atoms/engineAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';

export default function ClearTool() {
    const mainView = useRecoilValue(mainViewState);
    const environment = useRecoilValue(environmentState);
    const selectedROI = useRecoilValue(selectedROIState);
    const theme = useTheme();
    const themePrimaryColor = theme.palette.primary.main;
    const selectedStructure = environment?.structureSet?.getRoi(selectedROI);
    const selectedStructureName = selectedStructure?.name;
    const selectedStructureColorRgb = `rgb(${selectedStructure?.color
        .map((c) => c * 255)
        .join(',')})`;
    const structureApprovalStatus =
        selectedStructure?.dicomParent.approvalStatus;
    const structureSet = useRecoilValue(currentStructureSetState);

    const hasStructures = !!structureSet?.roiArray?.length;
    const applyOperationDisabled = selectedROI === -1 || !hasStructures;

    const applyClearContoursOnSliceOperation = () => {
        if (structureApprovalStatus === DICOMApprovalStatus.APPROVED) {
            enqueueSnackbar(
                'Clearing slices is not allowed for this ROI as it has already been reviewed.',
                { variant: 'warning' }
            );
            return;
        }
        if (environment && selectedStructureName && mainView) {
            const planeCoordinates = mainView.position[2];
            environment.clearContourOnSlice(
                selectedStructureName,
                planeCoordinates
            );
        }
    };

    return (
        <React.Fragment>
            {hasStructures && typeof selectedStructureName !== 'undefined' ? (
                <Box>
                    <Stack
                        direction="row"
                        alignItems="center"
                        mb={2}
                        gap={0.75}
                    >
                        <Typography variant="h6">
                            <FormattedMessage
                                id="clearToolContourSlice"
                                values={{
                                    selectedStructureName: (
                                        <Box
                                            display="inline-flex"
                                            alignItems="center"
                                            gap={0.75}
                                            component="span"
                                        >
                                            <Box
                                                height={10}
                                                width={10}
                                                borderRadius="50%"
                                                sx={{
                                                    backgroundColor:
                                                        selectedStructureColorRgb,
                                                }}
                                            />
                                            <Typography
                                                variant="h6"
                                                component="span"
                                                sx={{
                                                    color: themePrimaryColor,
                                                }}
                                            >
                                                {selectedStructureName}
                                            </Typography>
                                            <Tooltip
                                                title={
                                                    <FormattedMessage id="clearToolGuide" />
                                                }
                                            >
                                                <Info fontSize="small" />
                                            </Tooltip>
                                        </Box>
                                    ),
                                }}
                            />
                        </Typography>
                    </Stack>

                    <Box display="flex" flexDirection="column" gap={2}>
                        <Button
                            variant="outlined"
                            color="primary"
                            fullWidth
                            disabled={applyOperationDisabled}
                            onClick={applyClearContoursOnSliceOperation}
                        >
                            <FormattedMessage
                                id="currentSlice"
                                values={{ selectedStructureName }}
                            />
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Box>
                    <Stack
                        direction="row"
                        alignItems="center"
                        mb={2}
                        gap={0.75}
                    >
                        <Typography variant="h6">
                            <FormattedMessage id="clearTool" />
                        </Typography>
                        <Tooltip
                            title={<FormattedMessage id="clearToolGuide" />}
                        >
                            <Info fontSize="small" />
                        </Tooltip>
                    </Stack>
                    <Typography>
                        <FormattedMessage id="pleaseSelectStructure" />
                    </Typography>
                </Box>
            )}
        </React.Fragment>
    );
}
